import type { ArticleElement, ArticleManager, ArticleScreenshotOptions } from '@easterngraphics/wcf/modules/cf';
import type { Application } from '@easterngraphics/wcf/modules/core';
import type { SceneElement } from '@easterngraphics/wcf/modules/core/mdl';
import type { EaiwsSession } from '@easterngraphics/wcf/modules/eaiws';
import type { AbstractEventListener} from '@easterngraphics/wcf/modules/utils';
import type { EaiwsBasket } from '@egr/xbox/base-app/EaiwsBaskets';

import { Color4, type Mesh } from '@babylonjs/core';

import { getContent } from './Download';
import { ImageFormat } from './FileFormats';
import { developmentMode } from './ReactScriptHelper';

import { BASKET_COLUMN_ID_USER_IMAGE, type Basket } from '@easterngraphics/wcf/modules/bsk';
import { MainArticleElement, UserArticleElement } from '@easterngraphics/wcf/modules/cf';
import { ItemField, type ExchangeRate } from '@easterngraphics/wcf/modules/eaiws/basket';
import { SaveSessionOptions } from '@easterngraphics/wcf/modules/eaiws/session';
import { ListenableEvent } from '@easterngraphics/wcf/modules/utils';
import { ajax } from '@easterngraphics/wcf/modules/utils/async';
import { isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

export async function syncPricingProcedure(basket: EaiwsBasket, articleManager: ArticleManager) {
    if (articleManager.pricingProcedure?.name !== basket.calculation.activePricingProcedure) {
        if (basket.calculation.activePricingProcedure == null) {
            articleManager.setPricingProcedure(null);
        } else {
            const pricingProcedure = await basket.calculation.getPricingProcedure(basket.calculation.activePricingProcedure);
            articleManager.setPricingProcedure(pricingProcedure);
        }
    }
}

export function updateNavigationAreaByMesh(
    mesh: Mesh | null,
    coreApp: Application,
    minValue: number,
    override?: number,
) {
    if (mesh == null) {
        return;
    }

    // add 10 meter offset
    const radius: number = (Math.max(mesh.scaling.x, mesh.scaling.z) * 0.5) + 10.0;
    coreApp.viewer.view.cameraControl.setNavigationArea(
        override ?? (
            Math.max(
                radius,
                minValue,
                coreApp.viewer.view.cameraControl.navigationArea.radius
            )
        )
    );
}

export async function getObkUrl(basket: Basket): Promise<string> {
    const saveOptions: SaveSessionOptions = new SaveSessionOptions();
    saveOptions.export = true;

    if (basket.calculation.activePricingProcedure != null) {
        saveOptions.saveLegacyCalculation = true;
        saveOptions.legacyCalculationName = basket.calculation.activePricingProcedure;
    }

    return basket.context.session.saveSession(null, saveOptions);
}

export function get3dCanvasPixelRatio(initOptions: Application['initOptions'], fallback: number = 1.0): number {
    if (initOptions.adaptToDeviceRatio) {
        return Math.min(window.devicePixelRatio, initOptions.maximumDeviceRatio);
    }

    return fallback;
}

export class CreationEvent<T, O> extends ListenableEvent<T, O> {
    private _value: T | undefined;

    public override addListener(listener: AbstractEventListener<T, O, void>): void {
        if (this._value != null) {
            return listener(this._value, this.mOwner);
        }
        return super.addListener(listener);
    }

    public override trigger(parameter: T): void {
        if (this._value != null && developmentMode) {
            console.warn('trigger should only be called once');
        }
        this._value = parameter;
        super.trigger(parameter);
    }
}

export async function setFilteredExchangeRates(exchangeRates: Array<ExchangeRate>, basketForFilter: EaiwsBasket) {
    if (basketForFilter.calculation.activatePricingProcedure == null) {
        return;
    }
    const allowedRates = await basketForFilter.calculation.getExchangeRates();
    return basketForFilter.calculation.setExchangeRates(exchangeRates.filter((value) => {
        const allowedRate = allowedRates.find((rate) => rate.currency === value.currency);
        return allowedRate != null && value.rate != null && allowedRate.rate !== value.rate;
    }));
}

async function getSnapshotForUserImage(
    element: ArticleElement,
    coreApp: Application,
    articleManager: ArticleManager,
) {
    if (element?.node == null) {
        return null;
    }

    const articleScreenshotOptions: ArticleScreenshotOptions = {
        width: 1000,
        height: 1000,
        pixelScaleFactor: get3dCanvasPixelRatio(coreApp.initOptions),
        backgroundColor: new Color4(1, 1, 1, 1),
        mimeType: 'image/jpeg',
        // freshly inserted articles may never have been rendered yet, so their material's
        // shader can still be compiling (async KHR_parallel_shader_compile) on the first render;
        // renderAsync spreads the MSAA passes across multiple ticks so the mesh isn't skipped as not-ready
        renderAsync: true,
    };

    await coreApp.model.materials.waitUntilTexturesLoaded();

    // if the user cancels configuration while textures are loading,
    // the article is undefined now and createArticleScreenshot would throw exception
    if (element?.node == null) {
        return;
    }

    return articleManager.createArticleScreenshot(element, articleScreenshotOptions, true);
}

export async function addArticleSnapshotAsUserImageIfRequired(
    element: SceneElement,
    coreApp: Application,
    articleManager: ArticleManager,
) {
    if (
        element instanceof MainArticleElement &&
        isNotNullOrEmpty(element.basketId)
    ) {
        await element.waitUntilGeometryUpdated();
        if (element.hasPlaceholderGeometry) {
            return;
        }

        const ofmlStatus = await element.getOfmlUpdateState();
        if (ofmlStatus === 'NoCatalog' || ofmlStatus === 'Invalid') {
            const snapshot = await getSnapshotForUserImage(element, coreApp, articleManager);
            const field = new ItemField();
            field.columnId = BASKET_COLUMN_ID_USER_IMAGE;
            field.itemId = element.basketId;
            const uploadUrl = await articleManager.context.session.getUploadURL('Import', ImageFormat.JPG.extension);
            await ajax<void>('PUT', uploadUrl, snapshot);
            field.data = await articleManager.context.session.importFile(uploadUrl);
            await articleManager.context.basket.setItemFields([field]);
        }
    }
}

async function shouldAddImage(element: MainArticleElement | UserArticleElement) {
    if (element instanceof UserArticleElement) {
        return true;
    }

    const ofmlStatus = await element.getOfmlUpdateState();

    return (ofmlStatus === 'NoCatalog' || ofmlStatus === 'Invalid');
}

export async function setImageAsUserImageIfNoCatalogData(
    session: EaiwsSession,
    element: SceneElement,
    imageUrl: string | undefined
) {
    try {
        if (
            element instanceof MainArticleElement &&
            isNotNullOrEmpty(imageUrl) &&
            isNotNullOrEmpty(element.basketId)
        ) {
            if (await shouldAddImage(element)) {
                const field = new ItemField();
                field.columnId = BASKET_COLUMN_ID_USER_IMAGE;
                field.itemId = element.basketId;
                const uploadUrl = await session.session.getUploadURL('Import', ImageFormat.PNG.extension);
                await ajax<void>('PUT', uploadUrl, await getContent(imageUrl));
                field.data = await session.session.importFile(uploadUrl);
                await session.basket.setItemFields([field]);
            }
        }
    } catch {
        /* no image */
    }
}